import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

const BRANDING_KEY = 'ozon_branding';
const BASE_URL = '/api';


function usePendingCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    function fetch_() {
      const token = localStorage.getItem('ozon_token');
      if (!token) return;
      fetch('/api/incoming-requests?status=pending', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : [])
        .then(d => setCount(Array.isArray(d) ? d.length : 0))
        .catch(() => {});
    }
    fetch_();
    const id = setInterval(fetch_, 60000);
    return () => clearInterval(id);
  }, []);
  return count;
}

function useBranding() {
  function readCache() {
    try { return JSON.parse(localStorage.getItem(BRANDING_KEY)) || {}; } catch { return {}; }
  }
  const [branding, setBranding] = useState(readCache);
  useEffect(() => {
    const token = localStorage.getItem('ozon_token');
    if (!token) return;
    fetch(`${BASE_URL}/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!data) return; localStorage.setItem(BRANDING_KEY, JSON.stringify(data)); setBranding(data); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const handler = () => setBranding(readCache());
    window.addEventListener('branding-update', handler);
    return () => window.removeEventListener('branding-update', handler);
  }, []);
  useEffect(() => {
    const name = branding.appName || 'Ozon WMS';
    document.title = name;
    const favicon = document.getElementById('favicon');
    if (!favicon) return;
    if (branding.logoDataUrl) {
      favicon.href = branding.logoDataUrl;
    } else {
      const canvas = document.createElement('canvas');
      canvas.width = 32; canvas.height = 32;
      const ctx = canvas.getContext('2d');
      ctx.font = '24px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('📦', 16, 16);
      favicon.href = canvas.toDataURL();
    }
  }, [branding]);
  return branding;
}

const navGroups = [
  {
    label: 'ANA MENÜ',
    items: [
      { to: '/', label: 'Dashboard', icon: '📊' },
    ],
  },
  {
    label: 'OPERASYON',
    items: [
      { to: '/orders',      label: 'Siparişler',  icon: '📦' },
      { to: '/products',    label: 'Ürünler',     icon: '🏷️' },
      { to: '/stock-hub',        label: 'Stok Yönetimi',    icon: '📦' },
      { to: '/supply-management', label: 'Tedarik Yönetimi', icon: '🔗' },
      { to: '/scan-inbound',      label: 'Tedarik Takibi',   icon: '🤝' },
      { to: '/delivery-tracking', label: 'Teslimat Takibi',  icon: '🚚' },
      { to: '/need-scan',         label: 'Alım Listesi',     icon: '🛒' },
    ],
  },
  {
    label: 'FİNANS & ANALİZ',
    items: [
      { to: '/analytics',       label: 'Analiz',            icon: '📈' },
      { to: '/profitability',   label: 'Karlılık',          icon: '💰' },
      { to: '/financials',      label: 'Finansal Detay',    icon: '📑' },
      { to: '/company-finance', label: 'Şirket Finansları', icon: '🏢' },
    ],
  },
  {
    label: 'SİSTEM',
    items: [
      { to: '/activity-log', label: 'Sipariş Aktiviteleri', icon: '📋' },
      { to: '/settings',     label: 'Ayarlar',              icon: '⚙️' },
    ],
  },
];

export default function Layout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const username  = localStorage.getItem('ozon_username') || 'admin';
  const branding  = useBranding();
  const pendingCount = usePendingCount();
  const appName   = branding.appName    || 'Ozon WMS';
  const appSub    = branding.appSubtitle || 'FBS Sipariş Yönetimi';
  const logoUrl   = branding.logoDataUrl || '';

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Sayfa değişince sidebar'ı kapat (mobil)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Sidebar açıkken scroll engelle
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  function handleLogout() {
    localStorage.removeItem('ozon_token');
    localStorage.removeItem('ozon_username');
    navigate('/login');
  }

  const SidebarContent = () => (
    <>
      {/* Logo / Başlık */}
      <div className="px-4 py-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          {logoUrl && <img src={logoUrl} alt="logo" className="w-8 h-8 object-contain rounded flex-shrink-0" />}
          <div className="min-w-0">
            <div className="text-base font-bold tracking-wide truncate">{appName}</div>
            <div className="text-xs text-slate-400 mt-0.5 truncate">{appSub}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-4">
            <div className="px-3 mb-1.5 text-xs font-semibold tracking-widest text-slate-500 uppercase">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                >
                  <span className="text-base leading-none">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {item.to === '/supply-management' && pendingCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {pendingCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Kullanıcı + çıkış */}
      <div className="px-3 py-3 border-t border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg">
          <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 uppercase flex-shrink-0">
            {username[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-300 truncate">{username}</div>
          </div>
          <button onClick={handleLogout} title="Çıkış Yap"
            className="text-slate-500 hover:text-red-400 transition-colors text-sm">
            ⏻
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-gray-50">

      {/* ── Masaüstü Sidebar (md ve üzeri) ──────────────────────────── */}
      <aside className="hidden md:flex w-56 bg-slate-900 text-white flex-col flex-shrink-0 h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {/* ── Mobil Overlay ───────────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Mobil Sidebar (drawer) ──────────────────────────────────── */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-slate-900 text-white flex flex-col z-50
        transform transition-transform duration-300 ease-in-out md:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Kapat butonu */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="absolute top-3 right-3 text-slate-400 hover:text-white text-xl p-1"
        >
          ✕
        </button>
        <SidebarContent />
      </aside>

      {/* ── Ana İçerik ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobil Top Bar */}
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-slate-900 text-white flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-slate-300 hover:text-white text-xl leading-none"
            aria-label="Menü"
          >
            ☰
          </button>
          <div className="flex items-center gap-2 min-w-0">
            {logoUrl && <img src={logoUrl} alt="logo" className="w-6 h-6 object-contain rounded flex-shrink-0" />}
            <span className="font-bold text-sm truncate">{appName}</span>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
